import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const CONTENT_IDEAS = [
  {
    title: "Property tours",
    description:
      "The bread and butter of estate agent Reels. Walk through a property highlighting the best features with a voiceover or text overlay. Keep it under 30 seconds for maximum reach.",
  },
  {
    title: "Just listed / Just sold",
    description:
      "Quick-fire announcement Reels with the property photos, price, and key details. These build your reputation as an active, successful agent in your area.",
  },
  {
    title: "Area guides",
    description:
      "Show off your local knowledge. Walk through a neighbourhood highlighting cafes, parks, schools, and transport. Position yourself as the local expert, not just a listing service.",
  },
  {
    title: "Market updates",
    description:
      "Share local market data, price trends, and what buyers or sellers should know right now. A talking-head format works well. Keep it factual and avoid overpromising.",
  },
  {
    title: "Behind the scenes",
    description:
      "Show your team at work: staging a property, putting up a board, or celebrating a completion. This humanises your brand and builds trust with potential clients.",
  },
  {
    title: "Tips for buyers and sellers",
    description:
      "Short, actionable tips like 'three things to do before your first viewing' or 'how to prepare for a valuation'. Educational content gets saved and shared, extending your reach.",
  },
]

const BEST_PRACTICES = [
  {
    title: "Hook in the first second",
    description:
      "Start with movement, a bold text overlay, or a striking room. Never open with a logo animation or intro sequence. The algorithm rewards watch time, and that starts with a strong hook.",
  },
  {
    title: "Use trending audio strategically",
    description:
      "Trending sounds boost discoverability. Use them for lifestyle and behind-the-scenes content. For property tours, a professional voiceover or original music usually performs better and feels more on-brand.",
  },
  {
    title: "Add text overlays",
    description:
      "Most people watch Reels with the sound off. Key details (price, bedrooms, location) should always appear as text on screen. Keep text large enough to read on a mobile.",
  },
  {
    title: "Post consistently",
    description:
      "Three to five Reels per week is the sweet spot for growth. Quality matters more than quantity, but the algorithm rewards accounts that post regularly. Batch-create content to stay ahead.",
  },
  {
    title: "Use relevant hashtags",
    description:
      "Mix broad hashtags (#propertyUK, #estateagent) with local ones (#bristolproperty, #cliftonhomes). Use 5-10 hashtags per post. Avoid hashtag stuffing; Instagram penalises it.",
  },
  {
    title: "Optimise your profile",
    description:
      "Your bio should say what you do, where you operate, and how to get in touch. Include a link to your website or a Linktree with your latest listings, valuation booking, and guides.",
  },
]

const STATS = [
  { stat: "2.35%", label: "average Reels engagement rate (vs 0.6% for feed posts)" },
  { stat: "90s", label: "maximum Reel length for best reach" },
  { stat: "3-5x", label: "more reach than static posts" },
  { stat: "68%", label: "of home buyers use social media in their search" },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "How often should estate agents post on Instagram Reels?",
    a: "Three to five times per week is ideal for consistent growth. If that feels too much, start with two Reels per week and build up. Consistency matters more than volume. Batch-creating content (filming multiple Reels in one session) makes this sustainable even for small teams.",
  },
  {
    q: "What is the best length for a property Reel?",
    a: "15-30 seconds for property tours and listing announcements. Instagram's algorithm currently favours shorter Reels for discoverability. Market updates and tips can run longer (45-60 seconds) because the audience is watching for the information, not just the visually appealing content.",
  },
  {
    q: "Do I need professional equipment for Instagram Reels?",
    a: "No. A recent smartphone with a steady hand is all you need to start. If you want to improve quality, a phone gimbal (around 80 pounds) eliminates shaky footage, and a clip-on microphone (around 20 pounds) dramatically improves audio for talking-head content. Professional lighting helps for indoor property tours.",
  },
  {
    q: "Should I use my personal account or create a business account?",
    a: "Always use an Instagram Business or Creator account. It gives you access to analytics (who watches, when, and for how long), the ability to run paid promotions, and contact buttons. You can convert an existing personal account without losing followers.",
  },
  {
    q: "How do I measure ROI from Instagram Reels?",
    a: "Track three things: reach (how many people see your content), engagement (likes, saves, shares, comments), and conversions (enquiries, valuation requests, website visits). Use Instagram Insights for the first two and UTM parameters or a unique landing page for conversions. Over time, you will see which content types drive actual business.",
  },
  {
    q: "Can I repurpose QuiteRight videos as Instagram Reels?",
    a: "Yes. QuiteRight renders videos in portrait format specifically for Instagram Reels and Stories. Download the portrait version and post it directly. The branded video with voiceover and music is ready to publish without any editing.",
  },
  {
    q: "What hashtags should estate agents use on Instagram?",
    a: "Combine broad property hashtags (#propertyUK, #estateagent, #homesforsale) with local ones (#bristolproperty, #cliftonhomes, #bathestateagent). Use 5-10 per post. Avoid banned or spammy hashtags. Check what successful agents in your area are using and adapt their approach.",
  },
  {
    q: "How do I grow my Instagram following as an estate agent?",
    a: "Post consistently (3-5 Reels per week), engage with local accounts (comment on posts from local businesses, community pages, and potential clients), use location tags on every post, and create content that people want to save and share. Growth takes 3-6 months of consistent effort. Avoid buying followers; it destroys your engagement rate.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <div className="divide-y divide-stone">
      {items.map((item) => (
        <details key={item.q} className="group py-5">
          <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
            {item.q}
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
            >
              <path
                fillRule="evenodd"
                d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
        </details>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function InstagramReelsEstateAgentsGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Instagram Reels for Estate Agents: The Complete Guide",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Instagram Reels for Estate Agents" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Instagram Reels for estate agents
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            How to use Instagram Reels to win instructions, attract buyers, and
            build your brand as an estate agent. Content ideas, best practices,
            and a strategy that works.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Why Reels */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why Reels matter for estate agents
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Instagram Reels get 3-5x more reach than static photo posts. For
              estate agents, that means more eyes on your listings, your brand,
              and your local expertise without spending a penny on advertising.
            </p>
            <p>
              The algorithm pushes Reels to people who do not already follow you,
              based on their interests and location. A property tour Reel in
              Clifton can reach thousands of potential buyers and vendors in
              Bristol who have never heard of your agency.
            </p>
            <p>
              Vendors are watching too. When a homeowner is choosing an agent,
              they Google you and check your social media. An active Instagram
              with polished property Reels signals that you are a modern,
              proactive agent who will market their home properly.
            </p>
          </div>
        </div>
      </section>

      {/* Content ideas */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              What to post
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Six content ideas that work
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CONTENT_IDEAS.map((idea, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {idea.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {idea.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Best practices */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Best practices
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              How to make Reels that perform
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BEST_PRACTICES.map((practice, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {practice.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {practice.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {STATS.map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Posting schedule */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            A simple weekly posting schedule
            <span className="text-red">.</span>
          </h2>
          <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-cream/50">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">Day</th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">Content type</th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {[
                  { day: "Monday", type: "Property tour", notes: "Your best new listing. Strong hook." },
                  { day: "Wednesday", type: "Tips or market update", notes: "Educational content builds authority." },
                  { day: "Friday", type: "Behind the scenes or area guide", notes: "Humanise your brand. Show personality." },
                ].map((row) => (
                  <tr key={row.day} className="hover:bg-cream/30">
                    <td className="px-4 py-3 font-medium text-charcoal">{row.day}</td>
                    <td className="px-4 py-3 text-slate">{row.type}</td>
                    <td className="px-4 py-3 text-slate">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-slate">
            Start with three posts per week and adjust based on what your
            analytics show. The best posting times for UK property content are
            typically 7-8am (commuters), 12-1pm (lunch break), and 7-9pm
            (evening browse).
          </p>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/guides/social-media-estate-agents", title: "Social media for estate agents", desc: "The complete platform-by-platform social media guide." },
              { href: "/guides/tiktok-estate-agents", title: "TikTok for estate agents", desc: "How to use TikTok for property marketing." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Property Reels in 60 seconds
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight creates Reels-ready property videos from your listings
            automatically. Paste a URL, choose a template, and download a video
            formatted for Instagram Reels with voiceover and music.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
